// PM POSHAN MDM Calculator — Calculation engine (ported from calculator.js)
import {
  getEffectivePeriod,
  isLaddooActive,
  getMenuDisplay,
  WEEKDAY_ITEMS,
  dayName,
} from "./mdmData";
import { academicYearForDate } from "./academicYear";

const CATEGORIES = ["balvatika", "primary", "upper_primary", "high_school"];

function getVisibleItems(date, category, period) {
  const weekday = date.getDay();
  const available = period.rates[category].items;

  if (date >= new Date("2025-05-01")) {
    const wItems = WEEKDAY_ITEMS[weekday] || [];
    const out = {};
    for (const n of wItems) {
      if (available[n]) out[n] = { ...available[n] };
    }
    return out;
  }
  const out = { ...available };
  if (!isLaddooActive(date, weekday) && out.Laddoo) delete out.Laddoo;
  return out;
}

function autoBalance(category, visibleItems, period, date) {
  const cat = period.rates[category];
  if (!cat.target_total || date < new Date("2025-05-01")) return visibleItems;

  let sumNonVeg = 0;
  for (const [n, d] of Object.entries(visibleItems)) {
    if (n !== "Vegetables") sumNonVeg += d.rate_per_student;
  }
  if (visibleItems.Vegetables) {
    const rate = Math.max(0, cat.target_total - sumNonVeg);
    visibleItems.Vegetables = {
      ...visibleItems.Vegetables,
      rate_per_student: Math.round(rate * 100) / 100,
      is_auto_balanced: true,
    };
  }
  return visibleItems;
}

function calcCategory(date, category, count, period) {
  if (!count) {
    return { category, student_count: 0, items: {}, subtotal: 0, per_student_total: 0, rice_kg: 0 };
  }
  let vis = getVisibleItems(date, category, period);
  vis = autoBalance(category, vis, period, date);

  const items = {};
  let subtotal = 0;
  let riceG = 0;
  for (const [name, d] of Object.entries(vis)) {
    const totalQty = d.qty_per_student * count;
    const totalCost = d.rate_per_student * count;
    items[name] = {
      qty_per_student: d.qty_per_student,
      unit: d.unit,
      rate_per_student: d.rate_per_student,
      total_quantity: totalQty,
      total_cost: totalCost,
      emoji: d.emoji,
      is_auto_balanced: !!d.is_auto_balanced,
    };
    subtotal += totalCost;
    if (name === "Rice") riceG += totalQty;
  }
  return {
    category,
    student_count: count,
    items,
    subtotal: Math.round(subtotal * 100) / 100,
    per_student_total: count > 0 ? Math.round((subtotal / count) * 100) / 100 : 0,
    rice_kg: Math.round((riceG / 1000) * 100) / 100,
  };
}

export function calculateReport(date, counts, overrides = null) {
  const period = getEffectivePeriod(date);
  if (!period) throw new Error("No rate period found for selected date.");

  // Deep-clone the period rates so admin overrides for this academic year
  // don't mutate the base rate periods.
  const workingPeriod = {
    ...period,
    rates: JSON.parse(JSON.stringify(period.rates)),
  };

  // Apply academic-year override if any
  if (overrides && overrides.length > 0) {
    const ay = academicYearForDate(date);
    const match = overrides.find((o) => o.academic_year === ay);
    if (match?.rates) {
      for (const cat of CATEGORIES) {
        if (match.rates[cat]) {
          const overrideCat = match.rates[cat];
          workingPeriod.rates[cat] = {
            ...workingPeriod.rates[cat],
            ...(overrideCat.target_total !== undefined ? { target_total: overrideCat.target_total } : {}),
            items: { ...workingPeriod.rates[cat].items, ...(overrideCat.items || {}) },
          };
        }
      }
    }
  }

  const categories = {};
  const aggregated = {};
  let grandTotal = 0;
  let totalStudents = 0;
  let totalRiceKg = 0;

  for (const c of CATEGORIES) {
    const r = calcCategory(date, c, counts[c] || 0, workingPeriod);
    categories[c] = r;
    grandTotal += r.subtotal;
    totalStudents += r.student_count;
    totalRiceKg += r.rice_kg;

    for (const [n, d] of Object.entries(r.items)) {
      if (!aggregated[n]) {
        aggregated[n] = { total_quantity: 0, total_cost: 0, unit: d.unit, emoji: d.emoji, avg_rate: 0 };
      }
      aggregated[n].total_quantity += d.total_quantity;
      aggregated[n].total_cost += d.total_cost;
    }
  }
  for (const [, d] of Object.entries(aggregated)) {
    if (d.total_quantity > 0) d.avg_rate = Math.round((d.total_cost / d.total_quantity) * 100) / 100;
  }

  const weekday = date.getDay();
  return {
    date,
    weekday_number: weekday,
    weekday_name: dayName(date),
    menu_display: getMenuDisplay(date),
    laddoo_active: isLaddooActive(date, weekday),
    categories,
    aggregated_items: aggregated,
    summary: {
      total_students: totalStudents,
      grand_total: Math.round(grandTotal * 100) / 100,
      avg_per_student: totalStudents > 0 ? Math.round((grandTotal / totalStudents) * 100) / 100 : 0,
      total_rice_kg: Math.round(totalRiceKg * 100) / 100,
    },
    period: workingPeriod,
    academic_year: academicYearForDate(date),
    is_future_projection: date > new Date(),
  };
}

// Helper to build a 15544 ODIMDM-style SMS text (approximation)
export function buildOdiMdmSms(result, udise = "") {
  const d = result.date;
  const dstr = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  const s = result.categories;
  const total = (s.primary?.student_count || 0) + (s.upper_primary?.student_count || 0);
  return `MDM ${udise || "<UDISE>"} ${dstr} P${s.primary?.student_count || 0} U${s.upper_primary?.student_count || 0} B${s.balvatika?.student_count || 0} H${s.high_school?.student_count || 0} TOT${total} RICE${result.summary.total_rice_kg}kg COST${result.summary.grand_total}`;
}
